import { useState, useEffect, useRef, useCallback } from 'react';
import { useCookies } from 'react-cookie';
import _ from 'lodash';
import { CalculateRating, CalculateRatingByCorrectness } from '../Utils';

interface QuizConfig {
  apiUrl: string;
  category: string;
  extraProps: Record<string, any>;
}

type Meta = {
  content_id?: string;
  stage?: string;
  totalTimeTaken?: number;
  totalCharacters?: number;
  totalQuestions?: number;
  rating?: string;
};

type CompletionLevel = 'normal' | 'good' | 'perfect' | null;

export function useQuizFSRSEngine(
  config: QuizConfig,
  handleQA: (qa: any) => void,
) {
  const [cookies] = useCookies(['user_name']);
  const [loading, setLoading] = useState(false);
  const [doneQuestions, setDoneQuestions] = useState<any[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<any>(null);
  // Read-only mirror of the internal upcoming-questions queue so modules like
  // TrueFalseModule can eagerly hydrate siblings of the current
  // (e.g. all sub-questions sharing the same display_card_id) and
  // render them together immediately without waiting for the engine to advance.
  // FSRS scheduling still advances one question at a time; this state is
  // purely for grouped-display layouts.
  const [pendingQuestions, setPendingQuestions] = useState<any[]>([]);

  const [isFinished, setIsFinished] = useState(true);
  const [finalRating, setFinalRating] = useState<string | null>(null);
  const [completionLevel, setCompletionLevel] =
    useState<CompletionLevel>(null);
  const [selfGrading, setSelfGrading] = useState<boolean>(false);
  // When `selfGrading` is on, this separate switch controls whether the GUI
  // shows the inline "Show Answer" + rating selector + Submit button.
  // Defaults to true so the existing behaviour is preserved when the caller
  // doesn't provide the flag at all. Set `extraProps.show_self_grading_controls`
  // to false when you want a pure manual flow (e.g. grading only via the
  // session-summary table and no per-question inline controls).
  const [showSelfGradingControls, setShowSelfGradingControls] =
    useState<boolean>(true);

  const [stats, setStats] = useState({
    total: 0,
    done: 0,
    correct: 0,
    totalTimeTaken: 0,
    totalCharacters: 0,
  });
  const [ratingSetting, setRatingSetting] = useState<any>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [meta, setMeta] = useState<Meta>({});
  const [sessionPhase, setSessionPhase] = useState<'initial' | 'redo'>(
    'initial',
  );

  const timerRef = useRef<number>(Date.now());

  // Master synchronous data queue source of truth
  const questionsRef = useRef<any[]>([]);
  const nextSeqRef = useRef<number>(1);
  // Live mirror of currentQuestion that NEVER gets wiped to {} on isFinished,
  // so recordAnswer() closure always spreads the real last-answered question
  // fields (id, question, answer, choices, seq...) into updatedCurrent instead
  // of spreading an empty object {} and producing a blank row at the end.
  const currentQuestionRef = useRef<any>(null);

  // Synchronous mirror of `doneQuestions` state. React state is stale inside a
  // single synchronous event frame (e.g. when submitAllRemainingInCard calls
  // recordAnswer 10 times in a loop), so recordAnswer must read/write this ref
  // to keep the accumulated graded-question list correct across calls.
  const doneQuestionsRef = useRef<any[]>([]);
  const setDone = useCallback((next: any[]) => {
    doneQuestionsRef.current = next;
    setDoneQuestions(next);
  }, []);

  // Sync helper: whenever the master questionsRef.current is mutated
  // (clear / push / shift-clone / cloneDeep for redo), mirror the
  // reference into the pendingQuestions React state so grouped-display
  // modules can peek at upcoming siblings without private ref access.
  // Uses shallow clone to force React to see a change.
  const mirrorPending = useCallback(() => {
    setPendingQuestions([...questionsRef.current]);
  }, []);

  // 1. Fetch Rating Settings on Mount
  useEffect(() => {
    if (!cookies.user_name || !config.category || config.category === '')
      return;

    const params = new URLSearchParams({
      ...config.extraProps,
      category: config.category,
    });

    fetch(`/api/v1/fsrs/rating-settings?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => setRatingSetting(data))
      .catch((err) => console.error('Failed to fetch rating settings:', err));
  }, [config.category, cookies.user_name]);

  useEffect(() => {
    setSelfGrading(
      config.extraProps.grading_policy === 'Forced Manual' ||
        config.extraProps.grading_policy === 'Manual Preferred',
    );
  }, [config.extraProps.grading_policy]);

  useEffect(() => {
    // Accept both snake_case (extraProps convention from FSRSLayoutFrame,
    // which forwards route-level props verbatim) and camelCase for
    // flexibility. Missing flag defaults to `true` (backward compatible).
    const raw = config.extraProps.showSelfGradingControls;
    if (raw === undefined || raw === null) {
      setShowSelfGradingControls(false);
      return;
    }
    if (typeof raw === 'boolean') {
      setShowSelfGradingControls(raw);
      return;
    }
    
    setShowSelfGradingControls(true);
  }, [ config.extraProps.showSelfGradingControls ]);

  useEffect(() => {
    if (isFinished) {
      setCurrentQuestion({});
      if(sessionPhase === "initial" && selfGrading === false) {
        setCompletionLevel(calculateCompletionLevel(doneQuestions));
      }
    }
  }, [isFinished, doneQuestions]);

  useEffect(() => {
    if (meta.totalQuestions) {
      setStats({
        total: meta.totalQuestions,
        done: 0,
        correct: 0,
        totalTimeTaken: 0,
        totalCharacters: 0,
      });
      // Streaming: At that point, there is no data in the data.
      if (sessionPhase === "initial") {
        countdownAndStartFirst();
      } else {
        startFirst();
      }
    }
  }, [meta.totalQuestions, sessionPhase]);

  // Retry helper used by startFirst when meta.totalQuestions has arrived via SSE
  // but the first question chunk hasn't arrived yet (stream ordering race),
  // so questionsRef is still empty.
  // Instead of a bounded retry-count poll (which can miss due to timer jitter),
  // we set a flag (`startPending`) which causes the "auto start when data arrives"
  // effect below to call startFirst later as soon as at least 1 question is
  // present in the queue. This matches React's declarative reactivity model.
  const startTriggeredRef = useRef<boolean>(false);
  const autoStartWhenReadyRef = useRef<boolean>(false);

  // 1.1 Set the status of the quiz
  // 2.2 Set all the state.
  const startFirst = useCallback(async () => {
    setLoading(false);
    if (questionsRef.current.length === 0) {
      if (Number(meta.totalQuestions || 0) > 0) {
        // SSE ordering case: meta chunk arrived before question chunks.
        // Ask the declarative auto-start effect to kick off startFirst as soon
        // as queue data is actually available (no fixed retry budget).
        autoStartWhenReadyRef.current = true;
        return;
      }
      setIsFinished(true);
      return;
    }

    startTriggeredRef.current = true;
    autoStartWhenReadyRef.current = false;
    setStats({
      total: meta.totalQuestions || 0,
      done: 0,
      correct: 0,
      totalTimeTaken: 0,
      totalCharacters: 0,
    });

    const first = questionsRef.current.shift();
    mirrorPending();
    const firstQA = handleQA(first) ?? first;

    setDone([firstQA]); // Once it is called, push the question to completed queue. 
    currentQuestionRef.current = firstQA;
    setCurrentQuestion(firstQA); // Set the current question to the caller.
    setIsFinished(false);
    timerRef.current = Date.now();
  }, [meta.totalQuestions]);

  // Declarative auto-start effect. Handles the SSE ordering race without
  // polling budget exhaustion: when the startFirst function gave up because
  // questionsRef was empty, it sets autoStartWhenReadyRef=true. Then as soon
  // as the next question chunk lands and causes pendingQuestions state to
  // change (length > 0), this effect re-runs and starts the first question.
  useEffect(() => {
    if (!autoStartWhenReadyRef.current) return;
    if (startTriggeredRef.current) return;
    if (questionsRef.current.length > 0) {
      startFirst();
    }
  }, [
    pendingQuestions.length,
    meta.totalQuestions,
    isFinished,
    currentQuestion,
    sessionPhase,
    startFirst,
  ]);

  const countdownAndStartFirst = async () => {
    if (meta.totalQuestions === 0) {
      console.log("no data in this phase? meta", meta);
      return;
    }

    let count = 2;
    setCountdown(count);

    await new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        count -= 1;
        if (count > 0) {
          setCountdown(count);
        } else {
          clearInterval(interval);
          setCountdown(null);
          resolve();
        }
      }, 1000);
    });

    console.log("Starting to process the first row");
    startFirst();
  };

  const calculateCompletionLevel = (rows: any[]): CompletionLevel => {
    if (rows.length === 0) {
      return null;
    }

    const incorrectCount = rows.filter((row) => row.is_correct === 1).length;
    if (incorrectCount === 0) {
      return 'perfect';
    }

    const accuracy = (rows.length - incorrectCount) / rows.length;
    if (accuracy >= 0.8) {
      return 'good';
    }

    return 'normal';
  };

  // Drop every session-scoped value so the next session starts from the same
  // clean slate as a page reload: no leftover questions, progress or results
  // from a previously selected category.
  const resetSession = useCallback(() => {
    questionsRef.current = [];
    setDone([]);
    currentQuestionRef.current = null;
    nextSeqRef.current = 1;
    startTriggeredRef.current = false;
    autoStartWhenReadyRef.current = false;

    setPendingQuestions([]);
    setCurrentQuestion({});
    setIsFinished(true);
    setLoading(false);
    setCountdown(null);
    setMeta({});
    setSessionPhase('initial');
    setFinalRating(null);
    setCompletionLevel(null);
    setStats({
      total: 0,
      done: 0,
      correct: 0,
      totalTimeTaken: 0,
      totalCharacters: 0,
    });
  }, [setDone]);

  // Start a new quiz session.
  // 1. Validate required user and rating settings before starting.
  // 2. Fetch quiz data from the backend using the current category and extra props.
  // 3. Handle both normal JSON responses and streaming SSE responses.
  // 4. Extract and store session meta information when it is available.
  // 5. Collect question rows into a local array and ensure each row has a `seq`.
  // 6. Run the countdown flow before handing control to the first question.
  // 7. Reset the session phase to `initial` after the first batch is ready.
  // Output: 
  //   1. meta
  //   2. questions
  //   3. countdown
  const startSession = useCallback(async () => {
    if (!cookies.user_name || !ratingSetting) return;

    setLoading(true);
    setCompletionLevel(null);
    nextSeqRef.current = 1;
    questionsRef.current = []; // Clear master queue reference
    mirrorPending();
    // Reset auto-start flags for a fresh session (the user may click
    // "Start Session" again after finishing a previous run).
    startTriggeredRef.current = false;
    autoStartWhenReadyRef.current = false;

    const params = new URLSearchParams({
      ...config.extraProps,
      category: config.category,
    });

    try {
      let data: any[] = [];

      // 01. Fetch the data from api and push it into questions
      const res = await fetch(`${config.apiUrl}?${params.toString()}`);
      const contentType = res.headers.get('content-type') || '';
      const isStreaming = contentType.includes('text/event-stream');

      if (isStreaming) {
        // 1. Create a Transformer that converts raw text chunks into parsed JSON objects
        let streamBuffer = '';

        // 2. Pass a clean Transformer object to the constructor
        const sseJsonTransformer = new TransformStream<string, any>({
          transform(chunk, controller) {
            // Append the incoming string chunk to our local buffer
            streamBuffer += chunk;

            const parts = streamBuffer.split('\n\n');
            streamBuffer = parts.pop() || '';

            for (const part of parts) {
              const trimmed = part.trim();
              if (trimmed && trimmed.startsWith('data: ')) {
                console.log('Streaming: ', trimmed);
                if (trimmed.startsWith('data: ')) {
                  try {
                    const json = JSON.parse(trimmed.replace(/^data:\s*/, ''));
                    controller.enqueue(json); // Safely push down the pipeline
                  } catch (e) {
                    console.error('Failed to parse stream JSON chunk:', e);
                  }
                }
              }
            }
          },
        });

        // 3. Chain them using pipeThrough!
        const decodedTextStream = res.body!
          .pipeThrough(new TextDecoderStream()) // Bytes -> Text
          .pipeThrough(sseJsonTransformer); // Text -> JSON Objects

        // 4. Pull data out from the end of the pipeline
        const pipelineReader = decodedTextStream.getReader();

        // Use 'let' instead of 'const' so the loop condition can be updated
        let questionItem: any;
        let isStreamDone = false;

        while (!isStreamDone) {
          // Pull a raw chunk out of the reader pipe
          const chunk = await pipelineReader.read();

          // Re-assign the outer control flags (NO 'const' or 'let' here!)
          questionItem = chunk.value;
          isStreamDone = chunk.done;

          // 1. If the reader tells us it's done, exit the loop cleanly
          if (isStreamDone) {
            console.log('Stream successfully closed by the server!');
            break;
          }

          // 2. Safely push valid questions to your local array
          // Check if it's the 'meta' row or a 'question' row
          if (questionItem.type === 'meta') {
            setMeta((prevMeta) => ({
              ...prevMeta,
              ...(questionItem.item || {}),
              totalQuestions: questionItem.item?.totalQuestions ?? prevMeta?.totalQuestions ?? 0,
            }));
            setSessionPhase('initial');
          } else if (questionItem.type === 'data') {
            const item = questionItem?.item ?? questionItem;
              if (!item) continue;

              const seq = item.seq ?? nextSeqRef.current;
              if (item.seq === undefined) nextSeqRef.current += 1;

              const newItem = { ...item, seq };

              // 1. Sync PUSH into our master source reference pointer instantly
              questionsRef.current.push(newItem);
              // 2. Mirror pending (batches fine — React dedupes setState calls in the same microtask).
              mirrorPending();
          } else {
            // Fallback if backend sends raw item directly
            console.error(
              'Unknown item type:',
              questionItem,
              ', data: ',
              questionItem,
            );
          }
        }
      } else {
        const body = await res.json();
        const rawData = body.data || [];
        setMeta({
          ...(body.meta || {}),
          totalQuestions: rawData.length,
        });
        setSessionPhase('initial');

        // Normalize the non-stream payload before seeding the queue.
        data = rawData.map((item: any) => {
          const seq = item.seq ?? nextSeqRef.current;
          if (item.seq === undefined) {
            nextSeqRef.current += 1;
          }

          return {
            ...item,
            seq,
          };
        });

        questionsRef.current = _.cloneDeep(data);
        mirrorPending();
        setMeta((prevMeta) => ({
          ...(body.meta || {}),
          ...prevMeta,
          totalQuestions: questionsRef.current.length,
        }));
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
    }
  }, [config, cookies.user_name, ratingSetting]);

  // 3. Record and grade the user's answer, update stats, and advance to the next question
  const recordAnswer = (
    isCorrect: boolean,
    response: string,
    explicitRating?: string,
  ) => {
    const now = Date.now();
    const timeTaken = now - timerRef.current;

    let rating: string;
    let markedCorrect: boolean;
    if (selfGrading && explicitRating) {
      rating = explicitRating;
      markedCorrect = rating !== 'again';
    } else {
      // Rating logic based on your existing criteria
      rating = CalculateRating(
        selfGrading ? true : isCorrect,
        timeTaken,
        currentQuestion?.answer?.length || 1,
        ratingSetting,
      );
      markedCorrect = selfGrading ? true : isCorrect;
    }

    const currentQ = currentQuestionRef.current ?? currentQuestion;
    const updatedCurrent = {
      ...(currentQ || {}),
      time_taken: timeTaken,
      is_correct: markedCorrect ? 0 : 1,
      response,
      rating,
    };

    const newDone = [...doneQuestionsRef.current.slice(0, -1), updatedCurrent];
    const advanceToNextQuestion = (queue: any[]) => {
      const nextQs = [...queue];
      let next = nextQs.shift();

      next = handleQA(next) ?? next;
      setDone([...newDone, next]);

      // Keep master pointer and state in unified sync
      questionsRef.current = nextQs;
      mirrorPending();

      currentQuestionRef.current = next;
      setCurrentQuestion(next);
      timerRef.current = Date.now();

      return { finished: false };
    };

    const retryUntilNextQuestion = (retriesLeft: number) => {
      if (questionsRef.current.length > 0) {
        advanceToNextQuestion(questionsRef.current);
        return;
      }

      if (retriesLeft <= 0) {
        const supportMessage =
          'Unable to load the remaining questions. Please contact support.';
        console.error(supportMessage, {
          totalQuestions: meta.totalQuestions,
          doneQuestions: doneQuestionsRef.current.length,
          currentDone: newDone.length,
        });
        setIsFinished(true);
        setDone(newDone);
        if (typeof window !== 'undefined') {
          window.alert(supportMessage);
        }
        return;
      }

      window.setTimeout(() => {
        retryUntilNextQuestion(retriesLeft - 1);
      }, 1000);
    };

    setStats((prev) => ({
      ...prev,
      done: prev.done + 1,
      correct: markedCorrect ? prev.correct + 1 : prev.correct,
      totalTimeTaken: prev.totalTimeTaken + timeTaken,
      totalCharacters:
        (meta.totalCharacters || 0) + (currentQuestion?.answer?.length || 0),
    }));

    if (questionsRef.current.length === 0) {
      // Background:
      // In the initial streaming phase, the user can answer faster than the next
      // question arrives in the local queue. When that happens, `questions` may be
      // temporarily empty even though `meta.totalQuestions` says more items are
      // expected. We retry for a short period only in the initial phase.
      // The redo phase uses a fully prepared local list, so it should finish
      // immediately instead of waiting for additional items.
      const totalQuestions = Math.max(Number(meta.totalQuestions), 0);
      const completedBeforeCurrent = Math.max(doneQuestionsRef.current.length - 1, 0);
      const completedAfterCurrent = newDone.length;
      
      // Retry only for the initial phase when the total count indicates that
      // more questions should still arrive from the stream.
      if (
        sessionPhase === 'initial' &&
        totalQuestions > completedAfterCurrent &&
        totalQuestions !== completedBeforeCurrent
      ) {
        setDone(newDone);
        retryUntilNextQuestion(10);
        return { finished: false, retrying: true };
      }

      setIsFinished(true);
      setDone(newDone);
      return { finished: true, finalData: newDone };
    }

    return advanceToNextQuestion(questionsRef.current);
  };

  const submitResults = async (elapsedMs: number) => {
    // 1. Calculate per-question review time for 'again' and 'difficult'
    const eligibleRatings = new Set(['again', 'difficult']);
    const totalEligible = doneQuestions.reduce((count, row) => {
      const rating = String(row?.rating || '').toLowerCase();
      return count + (eligibleRatings.has(rating) ? 1 : 0);
    }, 0);

    const perQuestionReviewMs =
      totalEligible > 0 ? Math.floor(elapsedMs / totalEligible) : 0;

    // 2. Assign computed review_time to the dataset
    doneQuestions.forEach((row) => {
      const rating = String(row?.rating || '').toLowerCase();
      if (eligibleRatings.has(rating)) {
        row.deep_learning_duration = perQuestionReviewMs;
      }
    });

    // 3. Prepare common API params
    const params = new URLSearchParams({
      ...config.extraProps,
      category: config.category,
    });

    // 4. Logic for First Submission (sessionPhase === "initial")
    if (sessionPhase === 'initial') {
      // Compute overall rating once so we can reuse it (e.g. for a toast)
      const overallRating = (meta as any)?.content_id
        ? CalculateRatingByCorrectness(
            doneQuestions,
            stats.totalTimeTaken,
            ratingSetting,
          )
        : 'again';
      // Decide payload shape: per-row vs. single summary
      const payload = (meta as any)?.content_id
        ? [
            {
              id: (meta as any)?.content_id,
              time_taken: stats.totalTimeTaken,
              rating: overallRating,
              stage: (meta as any).stage,
              deep_learning_duration: elapsedMs,
            },
          ]
        : doneQuestions.map((row) => ({
            id: row.id,
            time_taken: row.time_taken,
            rating: row.rating,
            stage: row.stage,
            deep_learning_duration: row.deep_learning_duration,
          }));

      try {
        const res = await fetch(
          `/api/v1/fsrs/review-data?${params.toString()}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data: payload }),
          },
        );

        if (!res.ok) throw new Error(res.statusText || 'Submit failed');

        if ((meta as any)?.content_id) {
          // Notify the user of the overall rating they received
          console.log(`Overall rating: ${overallRating}`);
          setFinalRating(overallRating);
        }
        setSessionPhase('redo');

        const reviewQuestions = _.filter(
          doneQuestions,
          (row) => row.is_correct === 1,
        );

        questionsRef.current = _.cloneDeep(reviewQuestions);
        mirrorPending();
        setMeta((prevMeta) => ({
          ...prevMeta,
          totalQuestions: questionsRef.current.length,
        }));
        return { success: true, phase: 'initial' };
      } catch (err) {
        console.error(err);
        throw err;
      }
    }
    // 5. Logic for Redo/Subsequent Submissions
    else {
      const stageTimeTaken: Record<string, number> = {};
      if ((meta as any)?.stage) {
        stageTimeTaken[(meta as any).stage] = doneQuestions.reduce(
          (sum, r) => sum + (Number(r?.time_taken) || 0),
          0,
        );
        stageTimeTaken['deep_learning_' + (meta as any).stage] = elapsedMs;
      } else {
        doneQuestions.forEach((row) => {
          const stageKey = row?.stage ?? 'unknown';
          const timeTaken = Number(row?.time_taken) || 0;
          const reviewDuration = Number(row?.deep_learning_duration) || 0;

          stageTimeTaken[stageKey] =
            (stageTimeTaken[stageKey] || 0) + timeTaken;
          stageTimeTaken['deep_learning_' + stageKey] =
            (stageTimeTaken['deep_learning_' + stageKey] || 0) + reviewDuration;
        });
      }

      const res = await fetch(
        `/api/v1/fsrs/redo-time-taken?${params.toString()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(stageTimeTaken),
        },
      );
      try {
        await res.text();
        console.log('Time registration complete');

        // Reset for next redo if items still failed
        const reviewQuestions = _.filter(
          doneQuestions,
          (row) => row.is_correct === 1,
        );
        questionsRef.current = _.cloneDeep(reviewQuestions);
        mirrorPending();
        setMeta((prevMeta) => ({
          ...prevMeta,
          totalQuestions: questionsRef.current.length,
        }));
        return { success: true, phase: 'redo' };
      } catch (err) {
        console.error(err);
        throw err;
      }
    }
  };

  // Start a redo round with the given wrong questions. Used by modules that
  // persist the first round themselves (instead of submitResults) but still
  // want the engine's built-in "redo wrong questions" loop.
  // Mirrors submitResults() initial->redo transition without posting to the
  // FSRS review-data sink. Subsequent redo rounds use submitResults() which
  // only sends per-round time to /api/v1/fsrs/redo-time-taken (no FSRS).
  const startRedoRound = useCallback(
    (wrongQuestions: any[]) => {
      const reviewQuestions = _.cloneDeep(wrongQuestions || []);
      questionsRef.current = reviewQuestions;
      mirrorPending();
      nextSeqRef.current = 1;
      setSessionPhase('redo');
      setMeta((prevMeta) => ({
        ...prevMeta,
        totalQuestions: reviewQuestions.length,
      }));
      setIsFinished(false);
      setLoading(false);
    },
    [mirrorPending],
  );

  return {
    countdown,
    completionLevel,
    stats,                // State to show in the GUI
    finalRating,
    isFinished,           // State to track if the quiz is finished.
    sessionPhase,         // 'initial' | 'redo' — which round we're in.
    selfGrading,          // Whether grading_mode is Manual/Forced Manual overall.
    showSelfGradingControls, // When selfGrading is on, whether to render
                          // the inline Show Answer + rating selector + Submit
                          // panel. Driven by extraProps.show_self_grading_controls.
    loading,              // Lock once start session.

    doneQuestions,        // The completed question is displayed in the resut page. 
    currentQuestion,
    // Mutable ref to the current question (synchronously updated inside
    // recordAnswer/advanceToNextQuestion). Used by grouped-display layouts
    // (e.g. TrueFalseModule's submit-all loop) that need to read the *latest*
    // current question within a single synchronous call frame, because the
    // state value `currentQuestion` is stale until React re-renders.
    currentQuestionRef,
    // Read-only snapshot of the upcoming-question queue (does not include
    // currentQuestion or doneQuestions). Intended for grouped-display layouts
    // like TrueFalseModule so they can eagerly hydrate ALL sibling sub-questions
    // of the current display_card_id on the same page without waiting for the
    // engine to advance one by one.
    pendingQuestions,

    setCurrentQuestion,   // 2 Remove it by decouple it from single choice. 

    // Actions
    startSession,
    resetSession,         // Wipe the session so a new category starts clean.
    recordAnswer,
    submitResults,
    startRedoRound,
    setStats,             // Manually change the stats from the GUI
  };
}
